/**
 * @file YourProfile.jsx
 * @description Collects and validates initial user profile information during onboarding
 *
 * @section Responsibilities
 * - Collect and validate user personal information
 * - Handle document uploads and verification
 * - Manage profile picture upload and cropping
 * - Validate contact information
 * - Track onboarding progress
 * - Handle form submission and validation
 *
 * @section Dependencies
 * - ApplicationContext: Global state and configuration
 * - FileUploadService: Document handling and storage
 * - ProfileValidation: Data validation and sanitization
 * - ImageProcessing: Profile picture handling
 * - OnboardingProgress: Progress tracking
 *
 * @section Integration Points
 * - Profile API: Data submission and retrieval
 * - Document Service: File upload and verification
 * - Storage Service: Media file handling
 * - Onboarding Flow: Progress tracking and navigation
 * - Validation Service: Data validation
 *
 * @section State Management
 * - profileData: User information and form state
 * - validationState: Form validation status
 * - uploadProgress: File upload status
 * - documentStatus: Document verification state
 * - imagePreview: Profile picture preview
 *
 * @section Events
 * - onProfileUpdate: Profile information changes
 * - onDocumentUpload: File upload handling
 * - onValidation: Form validation
 * - onImageCrop: Profile picture processing
 * - onSubmit: Form submission
 *
 * @section Props
 * @prop {Object} initialData - Initial profile information
 * @prop {Function} onComplete - Completion callback
 * @prop {Boolean} isEditMode - Controls edit/view mode
 * @prop {Function} onProgressUpdate - Progress tracking callback
 */

import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/your-profile.css";
import { Grid } from "@mui/material";
import React, { useState, useEffect } from "react";
import TextInput from "./../../components/forms/TextInput";
import SelectInput from "./../../components/forms/SelectInput";
import DateInput from "./../../components/forms/DateInput";
import CheckboxInput from "./../../components/forms/CheckboxInput";
import DgButton from "./../../components/DgButton";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import BallsBackground from "../../components/BallsBackground";
import { useForm } from "../../utils/useForm";
import Request from "../../utils/fetchUtils";
import Registration from "./Registration";

const mockInitialData = {
  name: "",
  email: "",
  repCode: "",
  options: false,
  applicationType: {
    type: "",
    newAccountForeignForm: {
      tradeAuthorization: {
        isTradeAuthorization: "",
      },
      accountType: "",
      suitabilityProfile: {
        timeHorizon: "",
        liquidityNeeds: "",
      },
      investmentProfile: {
        totalNetWorthUSD: {
          min: 0,
          max: 0,
        },
        liquidNetWorthUSD: {
          min: 0,
          max: 0,
        },
        annualIncomeUSD: {
          min: 0,
          max: 0,
        },
        investmentObjective: "",
        riskTolerance: "",
        investmentExperience: "",
      },
      applicants: [
        {
          identity: {
            name: {
              givenName: "",
              familyName: "",
            },
            dateOfBirth: "",
            citizenshipCountry: "",
            birthCountry: "",
            foreignTaxIdNumber: "",
            foreignTaxIdCountry: "",
          },
          w_8ben: {
            foreignTaxIdentificationNumber: "",
            treatyCountry: "",
          },
          contact: {
            homeAddress: {
              streetAddress: [""],
              country: "",
              city: "",
              postalCode: "",
            },
            phoneNumbers: [
              {
                phoneNumber: localStorage.getItem("phone"),
                phoneNumberType: "",
              },
            ],
            emailAddresses: [],
            mailingAddress: {
              streetAddress: [""],
              country: "",
              city: "",
              postalCode: "",
            },
          },
          disclosures: {
            isPoliticallyExposed: "NO",
            isControlPerson: "NO",
            isAffiliatedExchangeOrFINRA: "NO",
          },
          numDependents: 0,
          employment: {
            employer: "",
            positionEmployed: "",
            employmentStatus: "",
            businessAddress: {
              streetAddress: [""],
              country: "",
              city: "",
              state: "",
              postalCode: "",
            },
          },
          maritalStatus: "",
        },
      ],
    },
  },
};

const YourProfile = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState(mockInitialData);

  const [basicData, setBasicData] = useForm({
    fullName: "",
    ssn: "",
    phone: "",
  });

  const [birthday, setBirthday] = useForm({
    day: "",
    month: "",
    year: "",
  });

  const [employmentData, setEmploymentData] = useForm({
    employmentStatus: "",
    employer: "",
    employersAddress: "",
    occupation: "",
  });

  const [addressData, setAddressData] = useForm({
    street: "",
    city: "",
    zipCode: "",
    state: "",
    country: "",
  });

  const [financialInfoData, setFinancialInfoData] = useForm({
    annualIncome: "",
    netWorth: "",
    liquidNetWorth: "",
    source: "",
  });

  const [experienceData, setExperienceData] = useForm({
    years: "",
    investmentsExperience: [],
  });

  const mergeData = () => {
    const sessionData = JSON.parse(localStorage.session);
    sendInfo(data);
  };

  useEffect(() => {
    setData((prevState) => ({
      ...prevState,
      applicationType: {
        ...prevState.applicationType,
        newAccountForeignForm: {
          ...prevState.applicationType.newAccountForeignForm,
          applicants: [
            ...prevState.applicationType.newAccountForeignForm.applicants.map(
              (applicant, index) => {
                if (index === 0) {
                  return {
                    ...applicant,
                    identity: {
                      ...applicant.identity,
                      dateOfBirth: birthday,
                    },
                  };
                }
                return applicant;
              }
            ),
          ],
        },
      },
    }));
  }, [birthday]);

  const sendInfo = async (data) => {
    setLoading(true);

    const res = await Request(
      `/trading/application`,
      { ...data, contact: { ...data.contact, emailAddresses: [data.email] } },
      "POST"
    );

    await navigate("/onboarding/agreements");
  };

  const { fullName, ssn, phone } = basicData;
  const { day, month, year } = birthday;
  const { employmentStatus, employer, employersAddress, occupation } =
    employmentData;
  const { street, city, zipCode, state, country } = addressData;
  const { annualIncome, netWorth, liquidNetWorth, source } = financialInfoData;
  const { years, investmentsExperience } = experienceData;

  const navigate = useNavigate();

  const [myOptions, setMyOptions] = useState([
    { value: "option-1", text: "Option 1" },
    { value: "option-2", text: "Option 2" },
    { value: "option-3", text: "Option 3" },
  ]);

  const [investmentTypes, setInvestmentTypesOptions] = useState([
    { value: "stocks", text: "Stocks" },
    { value: "bonds", text: "Bonds" },
    { value: "options", text: "Options" },
    { value: "mutual-funds", text: "Mutual funds" },
  ]);

  const now = new Date();
  const currentYear = now.getFullYear();
  const minimumBirthYear = currentYear - 18;

  const handleChange = (field, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleNestedChange = (field, subField, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        [subField]: value,
      },
    }));
  };

  const handleApplicantChange = (index, field, value) => {
    const newApplicants = [
      ...data.applicationType.newAccountForeignForm.applicants,
    ];
    newApplicants[index] = {
      ...newApplicants[index],
      [field]: value,
    };
    setData((prevState) => ({
      ...prevState,
      applicationType: {
        ...prevState.applicationType,
        newAccountForeignForm: {
          ...prevState.applicationType.newAccountForeignForm,
          applicants: newApplicants,
        },
      },
    }));
  };

  const body = (
    <div className="your-profile">
      <BallsBackground>
        <div className="content container shadow1">
          <div className="progress-bar">
            <div className="progress"></div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>1</span>
                </div>
                <p>Your profile</p>
              </div>
            </div>
            <div className="step">
              <div className="title">
                <div className="number">
                  <span>2</span>
                </div>
                <p>Agreements</p>
              </div>
            </div>
            <div className="step">
              <div className="title">
                <div className="number">
                  <span>3</span>
                </div>
                <p>Documents</p>
              </div>
            </div>
          </div>
          <div className="inner-content">
            <div className="heading">
              <p className="subtitle">Step 1</p>
              <h1 className="title">Your profile</h1>
              <p className="description">Enter your information to register</p>
            </div>
            <Grid
              container
              rowSpacing={4}
              columnSpacing={{ xs: 0, sm: 2, md: 12 }}
            >
              <Grid item xs={12} sm={6}>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Full name",
                      name: "fullName",
                      value: data.name,
                      action: (e) => handleChange("name", e.target.value),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Email",
                      name: "email",
                      value: data.email,
                      action: (e) => handleChange("email", e.target.value),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Rep Code",
                      name: "repCode",
                      value: data.repCode,
                      action: (e) => handleChange("repCode", e.target.value),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <div className="form-field">
                  <CheckboxInput
                    customInput={{
                      label: "Options",
                      name: "options",
                      value: data.options,
                      action: (e) => handleChange("options", e.target.checked),
                      tooltipBool: false,
                    }}
                  ></CheckboxInput>
                </div>
                <div className="form-field">
                  <SelectInput
                    customInput={{
                      label: "Account Type",
                      name: "accountType",
                      value:
                        data.applicationType.newAccountForeignForm.accountType,
                      action: (e) =>
                        handleNestedChange(
                          "applicationType",
                          "accountType",
                          e.target.value
                        ),
                      tooltipBool: false,
                      disabled: true,
                    }}
                    data={[
                      {
                        text: "Cash",
                        value: "CASH",
                      },
                      {
                        text: "Margin",
                        value: "MARGIN",
                      },
                      {
                        text: "IRA",
                        value: "IRA",
                      },
                    ]}
                  ></SelectInput>
                </div>
                <div className="form-field">
                  <SelectInput
                    customInput={{
                      label: "Time Horizon",
                      name: "timeHorizon",
                      value:
                        data.applicationType.newAccountForeignForm
                          .suitabilityProfile.timeHorizon,
                      action: (e) =>
                        handleNestedChange(
                          "applicationType",
                          "suitabilityProfile",
                          {
                            ...data.applicationType.newAccountForeignForm
                              .suitabilityProfile,
                            timeHorizon: e.target.value,
                          }
                        ),
                      tooltipBool: false,
                      disabled: true,
                    }}
                    data={[
                      {
                        text: "Longest",
                        value: "LONGEST",
                      },
                      {
                        text: "Medium",
                        value: "MEDIUM",
                      },
                      {
                        text: "Shortest",
                        value: "SHORTEST",
                      },
                    ]}
                  ></SelectInput>
                </div>
                <div className="form-field">
                  <SelectInput
                    customInput={{
                      label: "Liquidity Needs",
                      name: "liquidityNeeds",
                      value:
                        data.applicationType.newAccountForeignForm
                          .suitabilityProfile.liquidityNeeds,
                      action: (e) =>
                        handleNestedChange(
                          "applicationType",
                          "suitabilityProfile",
                          {
                            ...data.applicationType.newAccountForeignForm
                              .suitabilityProfile,
                            liquidityNeeds: e.target.value,
                          }
                        ),
                      tooltipBool: false,
                      disabled: true,
                    }}
                    data={[
                      {
                        text: "Very Important",
                        value: "VERY_IMPORTANT",
                      },
                      {
                        text: "Somewhat Important",
                        value: "SOMEWHAT_IMPORTANT",
                      },
                      {
                        text: "Not important",
                        value: "NOT_IMPORTANT",
                      },
                    ]}
                  ></SelectInput>
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <p className="subtitle">Applicant Information</p>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Given Name",
                      name: "givenName",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .identity.name.givenName,
                      action: (e) =>
                        handleApplicantChange(0, "identity", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].identity,
                          name: {
                            ...data.applicationType.newAccountForeignForm
                              .applicants[0].identity.name,
                            givenName: e.target.value,
                          },
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Family Name",
                      name: "familyName",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .identity.name.familyName,
                      action: (e) =>
                        handleApplicantChange(0, "identity", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].identity,
                          name: {
                            ...data.applicationType.newAccountForeignForm
                              .applicants[0].identity.name,
                            familyName: e.target.value,
                          },
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <DateInput
                    month={birthday.month}
                    day={birthday.day}
                    year={birthday.year}
                    setBirthday={setBirthday}
                  />
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Citizenship Country",
                      name: "citizenshipCountry",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .identity.citizenshipCountry,
                      action: (e) =>
                        handleApplicantChange(0, "identity", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].identity,
                          citizenshipCountry: e.target.value,
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Birth Country",
                      name: "birthCountry",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .identity.birthCountry,
                      action: (e) =>
                        handleApplicantChange(0, "identity", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].identity,
                          birthCountry: e.target.value,
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Foreign Tax ID Number",
                      name: "foreignTaxIdNumber",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .identity.foreignTaxIdNumber,
                      action: (e) =>
                        handleApplicantChange(0, "identity", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].identity,
                          foreignTaxIdNumber: e.target.value,
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Foreign Tax ID Country",
                      name: "foreignTaxIdCountry",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .identity.foreignTaxIdCountry,
                      action: (e) =>
                        handleApplicantChange(0, "identity", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].identity,
                          foreignTaxIdCountry: e.target.value,
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <p className="subtitle">Contact Information</p>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Street Address",
                      name: "streetAddress",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .contact.homeAddress.streetAddress[0],
                      action: (e) =>
                        handleApplicantChange(0, "contact", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].contact,
                          homeAddress: {
                            ...data.applicationType.newAccountForeignForm
                              .applicants[0].contact.homeAddress,
                            streetAddress: [e.target.value],
                          },
                        }),
                      tooltipBool: false,
                      maxLength: 30,
                    }}
                  />
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "City",
                      name: "city",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .contact.homeAddress.city,
                      action: (e) =>
                        handleApplicantChange(0, "contact", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].contact,
                          homeAddress: {
                            ...data.applicationType.newAccountForeignForm
                              .applicants[0].contact.homeAddress,
                            city: e.target.value,
                          },
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Postal Code",
                      name: "postalCode",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .contact.homeAddress.postalCode,
                      action: (e) =>
                        handleApplicantChange(0, "contact", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].contact,
                          homeAddress: {
                            ...data.applicationType.newAccountForeignForm
                              .applicants[0].contact.homeAddress,
                            postalCode: e.target.value,
                          },
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Country",
                      name: "country",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .contact.homeAddress.country,
                      action: (e) =>
                        handleApplicantChange(0, "contact", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].contact,
                          homeAddress: {
                            ...data.applicationType.newAccountForeignForm
                              .applicants[0].contact.homeAddress,
                            country: e.target.value,
                          },
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Phone Number",
                      name: "phoneNumber",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .contact.phoneNumbers[0].phoneNumber,
                      action: (e) =>
                        handleApplicantChange(0, "contact", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].contact,
                          phoneNumbers: [
                            {
                              phoneNumber: e.target.value,
                              phoneNumberType: "WORK",
                            },
                          ],
                        }),
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
              </Grid>
            </Grid>
            <div className="separator"></div>
            <p className="subtitle">Disclosures</p>
            <Grid
              container
              rowSpacing={4}
              columnSpacing={{ xs: 0, sm: 2, md: 12 }}
            >
              <Grid item xs={12} sm={6}>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Is Politically Exposed",
                      name: "isPoliticallyExposed",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .disclosures.isPoliticallyExposed,
                      action: (e) =>
                        handleApplicantChange(0, "disclosures", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].disclosures,
                          isPoliticallyExposed: e.target.value,
                        }),
                      tooltipBool: false,
                      disabled: true,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Is Control Person",
                      name: "isControlPerson",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .disclosures.isControlPerson,
                      action: (e) =>
                        handleApplicantChange(0, "disclosures", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].disclosures,
                          isControlPerson: e.target.value,
                        }),
                      tooltipBool: false,
                      disabled: true,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Is Affiliated Exchange Or FINRA",
                      name: "isAffiliatedExchangeOrFINRA",
                      value:
                        data.applicationType.newAccountForeignForm.applicants[0]
                          .disclosures.isAffiliatedExchangeOrFINRA,
                      action: (e) =>
                        handleApplicantChange(0, "disclosures", {
                          ...data.applicationType.newAccountForeignForm
                            .applicants[0].disclosures,
                          isAffiliatedExchangeOrFINRA: e.target.value,
                        }),
                      tooltipBool: false,
                      disabled: true,
                    }}
                  ></TextInput>
                </div>
              </Grid>
            </Grid>
            <div className="button-container">
              {/* Replace the error display and button with your own implementation */}
              {/* {error && <div className="error">{error}</div>} */}
              <DgButton
                category={"primary"}
                size={"large"}
                text={"Next"}
                action={mergeData}
              >
                <ArrowForwardIosIcon className="icon next" />
              </DgButton>
            </div>
          </div>
        </div>
      </BallsBackground>
    </div>
  );

  return <Registration />;
};

export default YourProfile;
